// Sentry must initialize before React renders so it captures errors that
// occur during the very first render pass.
import "./sentry";
import { ApiClientProvider } from "@albertine/api-client";
import { addIntegration, tanstackRouterBrowserTracingIntegration } from "@sentry/react";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { config } from "./config.ts";

import {
    persister,
    persistOptions,
} from "./integrations/tanstack-query/persistence.ts";
import * as TanStackQueryProvider from "./integrations/tanstack-query/root-provider.tsx";

// Import the generated route tree
import { routeTree } from "./routeTree.gen.ts";

import "./styles.css";
import { AppMessageProvider } from "@albertine/wrapper-interop/react";

import { DEV_MEMBER_LOGIN_ENABLED } from "./lib/devMemberLogin/devMemberLoginEnabled.ts";
import { getKeycloakToken } from "./lib/getKeycloakToken.ts";
import { notifyParentUnauthorized } from "./lib/iframeKeycloakToken.ts";

import reportWebVitals from "./reportWebVitals.ts";

const TanStackQueryProviderContext = TanStackQueryProvider.getContext();

// Register the router instance for type safety
declare module "@tanstack/react-router" {
    interface Register {
        router: ReturnType<typeof createAppRouter>;
    }
}

void completeDevMemberLogin().finally(renderApp);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// The router reads the URL when it's created and must not see the login
// parameters that Keycloak adds when it redirects back, so the login is
// completed before the router is created.
async function completeDevMemberLogin() {
    if (!DEV_MEMBER_LOGIN_ENABLED) {
        return;
    }

    const devMemberLogin = await import("./lib/devMemberLogin/devMemberLogin.ts");
    if (await devMemberLogin.completeDevMemberLogin()) {
        // The persisted responses may belong to the previously logged in
        // member.
        await persister.removeClient();
    }
}

function createAppRouter() {
    return createRouter({
        routeTree,
        context: {
            ...TanStackQueryProviderContext,
        },
        defaultPreload: "intent",
        scrollRestoration: true,
        defaultStructuralSharing: true,
        defaultPreloadStaleTime: 0,
        defaultNotFoundComponent: () => <div>404</div>,
    });
}

function renderApp() {
    const router = createAppRouter();

    // Sentry is already initialized (see ./sentry); register browser tracing here
    // now that the router exists so navigation transactions get route names.
    addIntegration(tanstackRouterBrowserTracingIntegration(router));

    const rootElement = document.getElementById("app");
    if (!rootElement || rootElement.innerHTML) {
        return;
    }

    const root = ReactDOM.createRoot(rootElement);
    root.render(
        <StrictMode>
            <PersistQueryClientProvider
                client={TanStackQueryProviderContext.queryClient}
                persistOptions={persistOptions}
            >
                <ApiClientProvider
                    baseUrl={config.apiServerBaseUrl}
                    queryClient={TanStackQueryProviderContext.queryClient}
                    getFirebaseAuthToken={async () => null}
                    getKeycloakToken={getKeycloakToken}
                    onUnauthorized={() => {
                        void persister.removeClient();
                        notifyParentUnauthorized();
                    }}
                    disableDevtools
                >
                    <AppMessageProvider>
                        <RouterProvider router={router} />
                    </AppMessageProvider>
                </ApiClientProvider>
            </PersistQueryClientProvider>
        </StrictMode>,
    );
}
